import { Box, Typography } from "@mui/material";
import type { DateTime } from "luxon";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import type { OpenShift, ShiftItem } from "../../../../utils/models";
import { MedicalDayCard } from "./MedicalDayCard";
import { PickRequestsFAB } from "./PickRequestsFAB";
import { useShiftWeekListMedicalState } from "./useShiftWeekListMedicalState";

type ShiftWeekListMedicalProps = {
	shifts: ShiftItem[];
	allOpenShifts: Record<string, OpenShift[]>;
	staffScheduleDisplayUntilDate?: DateTime;
};

export const ShiftWeekListMedical: FC<ShiftWeekListMedicalProps> = ({ shifts, allOpenShifts, staffScheduleDisplayUntilDate }) => {
	const { t } = useTranslation();
	const { groupedShifts, expandedDays, getDaySummary, groupShiftsBySection, toggleDay } = useShiftWeekListMedicalState(shifts, allOpenShifts);
	const [requestIdsToPick, setRequestIdsToPick] = useState<string[]>([]);

	const clearRequestIdsToPick = () => {
		setRequestIdsToPick([]);
	};

	const handleCheckBoxChanged = (requestId: string, checked: boolean) => {
		setRequestIdsToPick((prev) => {
			if (checked) {
				return prev ? [...prev, requestId] : [requestId];
			}
			return prev ? prev.filter((id) => id !== requestId) : [];
		});
	};

	const weekEntries = Object.entries(groupedShifts);
	const firstBlockedIndex = staffScheduleDisplayUntilDate
		? weekEntries.findIndex(([, { weekStart }]) => weekStart.startOf("day") > staffScheduleDisplayUntilDate.startOf("day"))
		: -1;
	const publishedEntries = firstBlockedIndex === -1 ? weekEntries : weekEntries.slice(0, firstBlockedIndex);

	return (
		<Box sx={{ display: "flex", gap: 1, flexDirection: "column", width: "100%", borderRadius: "8px" }}>
			<PickRequestsFAB requestIdsToPick={requestIdsToPick} onSuccess={clearRequestIdsToPick} />
			{publishedEntries.map(([weekKey, { weekStart, shifts: weekShifts, shiftCount }]) => (
				<Box key={weekKey} sx={{ mb: 4 }}>
					{/* Week Header - non-collapsible */}
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 2,
							color: "text.secondary",
						}}
					>
						<Typography sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 550, fontSize: "12px" })} variant="subtitle2">
							{weekKey}
						</Typography>
						<Typography sx={(theme) => ({ color: theme.palette.primary.main, fontWeight: 550 })} variant="subtitle2">
							{t("my.shifts")} ({shiftCount})
						</Typography>
					</Box>

					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: 1,
						}}
					>
						{/* Day Cards - each day is collapsible */}
						{Array.from({ length: 7 }).map((_, index) => {
							const currentDate = weekStart.plus({ days: index });
							const dateKey = currentDate.toFormat("yyyy-MM-dd");
							const dayShifts = weekShifts[dateKey];
							const isExpanded = expandedDays.includes(dateKey);
							const summary = getDaySummary(dayShifts, dateKey);
							const dateRequests = allOpenShifts[dateKey] || [];
							const shiftsBySection = groupShiftsBySection(dayShifts);

							return (
								<MedicalDayCard
									key={dateKey}
									dateKey={dateKey}
									currentDate={currentDate}
									isExpanded={isExpanded}
									summary={summary}
									dateRequests={dateRequests}
									shiftsBySection={shiftsBySection}
									onToggle={toggleDay}
									onCheckBoxChanged={handleCheckBoxChanged}
									requestIdsToPick={requestIdsToPick}
								/>
							);
						})}
					</Box>
				</Box>
			))}
		</Box>
	);
};
